import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "ButtonGroup API",
  "title": "ButtonGroup",
  "props": ["ButtonGroupProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const ButtonGroupProps = makeShortcode("ButtonGroupProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="buttongroup" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  A ButtonGroup is a layout component for <Link to="/api/button" mdxType="Link">Buttons</Link>,{' '}
  <Link to="/api/icon-button" mdxType="Link">Icon Buttons</Link> and{' '}
  <Link to="/api/dropdown" mdxType="Link">Dropdown Buttons</Link>. It's a helper for aligning 2
  or more buttons together with appropriate spacing.
      </LeadParagraph>
      <h2>{`Default`}</h2>
      <p>{`By default, the ButtonGroup will align the buttons horizontally and to the left.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { ButtonGroup, Button, ButtonColor } from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Button color={ButtonColor.secondary}>Cancel</Button>
      <Button color={ButtonColor.primary}>Save</Button>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Orientation`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`orientation`}</inlineCode>{` prop can be used to display buttons vertically, instead of horizontally. The `}<inlineCode parentName="p">{`orientation`}</inlineCode>{` prop accepts either `}<inlineCode parentName="p">{`horizontal`}</inlineCode>{` (default) or `}<inlineCode parentName="p">{`vertical`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { ButtonGroup, Button, ButtonGroupOrientation } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <ButtonGroup orientation={ButtonGroupOrientation.horizontal}>
        <Button>1</Button>
        <Button>2</Button>
        <Button>3</Button>
      </ButtonGroup>
      <br />
      <br />
      <ButtonGroup orientation={ButtonGroupOrientation.vertical}>
        <Button>4</Button>
        <Button>5</Button>
        <Button>6</Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Alignment`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`alignment`}</inlineCode>{` prop can be used to display buttons in different positions. The `}<inlineCode parentName="p">{`alignment`}</inlineCode>{` prop accepts: `}<inlineCode parentName="p">{`left`}</inlineCode>{` (default), `}<inlineCode parentName="p">{`right`}</inlineCode>{`, `}<inlineCode parentName="p">{`center`}</inlineCode>{`, `}<inlineCode parentName="p">{`apart`}</inlineCode>{` and `}<inlineCode parentName="p">{`fill`}</inlineCode>{`.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`apart`}</inlineCode>{` prop should only be used with the `}<inlineCode parentName="p">{`horizontal`}</inlineCode>{` orientation.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonGroup,
  Button,
  ButtonGroupOrientation,
  ButtonGroupAlignment,
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <>
      <ButtonGroup
        orientation={ButtonGroupOrientation.horizontal}
        alignment={ButtonGroupAlignment.right}
      >
        <Dropdown>
          <DropdownButton>1</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Button>2</Button>
        <Button>3</Button>
      </ButtonGroup>
      <br />
      <br />
      <ButtonGroup
        orientation={ButtonGroupOrientation.horizontal}
        alignment={ButtonGroupAlignment.center}
      >
        <Dropdown>
          <DropdownButton>1</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Button>2</Button>
        <Button>3</Button>
      </ButtonGroup>
      <br />
      <br />
      <ButtonGroup
        orientation={ButtonGroupOrientation.horizontal}
        alignment={ButtonGroupAlignment.apart}
      >
        <Dropdown>
          <DropdownButton>1</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Button>2</Button>
        <Button>3</Button>
      </ButtonGroup>
      <br />
      <br />
      <ButtonGroup
        orientation={ButtonGroupOrientation.horizontal}
        alignment={ButtonGroupAlignment.fill}
      >
        <Dropdown>
          <DropdownButton>1</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Button>2</Button>
        <Button>3</Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`No Space`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`noSpace`}</inlineCode>{` prop should only be used with the `}<inlineCode parentName="p">{`horizontal`}</inlineCode>{` orientation.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonGroup,
  Button,
  ButtonGroupOrientation,
  ButtonColor,
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <>
      <ButtonGroup
        orientation={ButtonGroupOrientation.horizontal}
        noSpace
        color={ButtonColor.secondary}
      >
        <Button>1</Button>
        <Button>2</Button>
        <Button>3</Button>
      </ButtonGroup>
      <br />
      <br />
      <ButtonGroup orientation={ButtonGroupOrientation.horizontal} noSpace>
        <Dropdown>
          <DropdownButton>1</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Dropdown>
          <DropdownButton>2</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 2</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Dropdown>
          <DropdownButton>3</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 3</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Color, Variant, Size, isInverse`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`color`}</inlineCode>{`, `}<inlineCode parentName="p">{`variant`}</inlineCode>{` and `}<inlineCode parentName="p">{`size`}</inlineCode>{` props can be used on the ButtonGroup to set the default value for all the buttons inside it. If a Button in the group also sets that prop, the one in the Button will be visible.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonGroup,
  Button,
  ButtonColor,
  ButtonVariant,
  ButtonSize,
  Container,
} from 'react-magma-dom';

export function Example() {
  return (
    <Container isInverse style={{ padding: '12px' }}>
      <ButtonGroup
        color={ButtonColor.primary}
        variant={ButtonVariant.solid}
        size={ButtonSize.large}
        isInverse
      >
        <Button>1</Button>
        <Button color={ButtonColor.danger}>2</Button>
        <Button>3</Button>
        <Button>4</Button>
        <Button variant={ButtonVariant.link}>5</Button>
      </ButtonGroup>
    </Container>
  );
}
`}</code></pre>
      <h2>{`Small Screen Sizes`}</h2>
      <p>{`This is an example of how to handle changing the orientation of the `}<inlineCode parentName="p">{`ButtonGroup`}</inlineCode>{` for certain Breakpoints.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonGroup,
  Button,
  ButtonGroupOrientation,
  BreakpointsContainer,
  Breakpoint,
  BreakpointScreenSize,
} from 'react-magma-dom';

export function Example() {
  return (
    <BreakpointsContainer>
      <Breakpoint screenSize={BreakpointScreenSize.xs}>
        <ButtonGroup orientation={ButtonGroupOrientation.vertical}>
          <Button>1</Button>
          <Button>2</Button>
          <Button>3</Button>
          <Button>4</Button>
          <Button>5</Button>
        </ButtonGroup>
      </Breakpoint>

      <Breakpoint screenSize={BreakpointScreenSize.small}>
        <ButtonGroup orientation={ButtonGroupOrientation.horizontal}>
          <Button>1</Button>
          <Button>2</Button>
          <Button>3</Button>
          <Button>4</Button>
          <Button>5</Button>
        </ButtonGroup>
      </Breakpoint>
    </BreakpointsContainer>
  );
}
`}</code></pre>
      <h2>{`ButtonGroup Props`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <ButtonGroupProps mdxType="ButtonGroupProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      